import React, {Component} from 'react';
import {connectToStore} from './Provider';
import BlockContent from '@sanity/block-content-to-react';
import Flickity from './Flickity';
import {Transition} from 'react-transition-group';
import classNames from 'classnames';
import find from 'lodash/find';
import min from 'lodash/min';
import {centsToPrice} from '../utils/filters';
import InventoryProductProvider from './InventoryProductProvider';
import Reveal from './Reveal';
import ProductHeroForm from './ProductHeroForm';
import ImageGalleryModal from './ImageGalleryModal';
import ProductOptionsForm from './ProductOptionsForm';
import {opacityFadeDuration} from '../config/animation';
import links from '../serializers/links';

const MAX_SLIDES = 4; // 4 slides
const SLIDE_MARGIN_HEIGHT = 80;

const DEFAULT_STYLE = {
	transition: `opacity ${opacityFadeDuration}ms ease-in-out`,
	opacity: 0,
};

const TRANSITION_STYLES = {
	entering: {opacity: 0},
	entered:  {opacity: 1},
	exited:   {opacity: 0},
	exiting:  {opacity: 0},
};

const SLIDE_OPTIONS = {
	cellAlign: 'center',
	contain: true,
	wrapAround: true,
	pageDots: false,
	prevNextButtons: false,
	selectedAttraction: 0.1,
	friction: 0.8
};

class ProductHero extends Component {
	constructor(props) {
		super();

		let activeVariantId = null;

		if (props.variants && props.variants.length > 0) {
			activeVariantId = props.variants[0].variantId;
		}

		this.state = {
			activeVariantId,
			activeImageIndex: 0,
		};
	}

	componentDidMount = () => {
		window.affirm && window.affirm.ui.ready(() => {
			window.affirm.ui.refresh();
		});
	}

	componentDidUpdate = (prevProps, prevState) => {
		if (this.state.activeImageIndex !== prevState.activeImageIndex) {
			this.mobileSlider && this.mobileSlider.select(this.state.activeImageIndex);
			this.modalSlider && this.modalSlider.select(this.state.activeImageIndex);
		}

		if (this.state.activeVariantId !== prevState.activeVariantId) {
			window.affirm && window.affirm.ui.refresh();
		}
	}

	setAssetIndex = activeImageIndex => {
		this.setState({activeImageIndex});
	}

	onClickPrevious = () => {
		const {variants} = this.props;
		const {activeVariantId, activeImageIndex} = this.state;
		const activeVariant = find(variants, {variantId: activeVariantId});

		if (activeImageIndex <= 0) {
			this.setState({
				activeImageIndex:  activeVariant.heroAssets.length - 1,
			});
		} else {
			this.setState({
				activeImageIndex: activeImageIndex - 1
			});
		}
	}

	onClickNext = () => {
		const {variants} = this.props;
		const {activeVariantId, activeImageIndex} = this.state;
		const activeVariant = find(variants, {variantId: activeVariantId});

		if (activeImageIndex >= activeVariant.heroAssets.length - 1) {
			this.setState({
				activeImageIndex: 0,
			});
		} else {
			this.setState({
				activeImageIndex: activeImageIndex + 1
			});
		}
	}

	onSelectOption = (optionIndex, name, value) => {
		const {variants} = this.props;
		const {activeVariantId} = this.state;
		const activeVariant = find(variants, {variantId: activeVariantId});

		const newVariantOptions = [
			activeVariant.option1,
			activeVariant.option2,
			activeVariant.option3,
		];

		newVariantOptions[optionIndex] = value;

		const newVariant = find(variants, variant => {
			for (let i = 0; i < 3; i++) {
				const newOption = newVariantOptions[i];
				const currentOption = variant[`option${i + 1}`];

				if (
					newOption &&
					newOption !== currentOption
				) {
					return false;
				}
			}

			return true;
		});

		if (!newVariant) {
			console.warn('Error: did not find variant in ProductHero form.');
			return;
		}

		this.setState({
			activeVariantId: newVariant.variantId,
			activeImageIndex: 0,
		});
	}

	render() {
		const {
			brand,
			title,
			cents,
			compareAtCents,
			variants,
			productId,
			optionDropdowns,
			isProductHeroModalOpen,
			openProductHeroModal,
			closeProductHeroModal,
			priceMatchCta,
			notifyMeCta,
			priceMatchDescription,
			enableAuthorizedDealer,
			authorizedDealerTitle,
			authorizedDealerSubtitle,
			authorizedDealerLogo,
			guaranteeTitle,
			guaranteeColumns = [],
		} = this.props;

		const {
			activeVariantId,
			activeImageIndex,
		} = this.state;

		const activeVariant = find(variants, {variantId: activeVariantId});
		const productCompareAtCents = activeVariant ? activeVariant.compareAtCents : compareAtCents;
		const productCents = activeVariant ? activeVariant.cents : cents;
		const activeHeroAsset = (activeVariant && activeVariant.heroAssets) && activeVariant.heroAssets[activeImageIndex];
		const selectedOptions = activeVariantId ? [activeVariant.option1, activeVariant.option2, activeVariant.option3] : [null, null, null];

		let heroAsset;

		if (activeHeroAsset && activeHeroAsset._type === 'image') {
			heroAsset = (
				<div
					className="image size--1x1 multiply">
					<picture>
						<img
							src={`${activeHeroAsset.url}?w=1000&auto=format`}
							alt={activeHeroAsset.alt}
						/>
					</picture>
				</div>
			);
		} else if (activeHeroAsset && activeHeroAsset._type === 'video') {
			const __html = (`
				<video
					class="full-width"
					muted
					autoplay
					playsinline
					preload
					loop
					poster="${activeHeroAsset.posterImage}?w=1000&auto=format">
					<source
						src="${activeHeroAsset.url}"
						type="video/mp4"
					></source>
				</video>
			`);

			heroAsset = (
				<div
					className="full-width"
					dangerouslySetInnerHTML={{__html}}
				/>
			);
		}

		const imageButtons = activeVariant && activeVariant.heroAssets && activeVariant.heroAssets.map((asset, index) => {
			if (asset._type === 'image') {
				return (
					<button
						title={asset.alt}
						onClick={() => this.setAssetIndex(index)}
						name={asset.alt}
						key={asset._key}
						className={classNames('db product__hero-asset-button', {
							active: index === activeImageIndex,
						})}>
						<picture className="full-width">
							<img
								className="full-width"
								alt={asset.alt}
								src={`${asset.url}?w=160&auto=format`}
							/>
						</picture>
					</button>
				);
			} else {
				return (
					<button
						onClick={() => this.setAssetIndex(index)}
						title={asset.alt}
						name={asset.alt}
						key={asset._key}
						className={classNames('db product__hero-asset-button pr video__poster', {
							active: index === activeImageIndex,
						})}>
						<picture className="full-width">
							<img
								className="full-width"
								alt={asset.alt}
								src={`${asset.posterImage}?w=160&auto=format`}
							/>
						</picture>
						<span className="db video__poster__play small" />
						<img
							className="db video__poster__icon small"
							alt="play"
							src="/images/icon-play.svg"
						/>
					</button>
				);
			}
		});

		let maxTransform = 0;
		let slideshowTransform = 0;
		let transform = 0;

		if (activeVariant && activeVariant.heroAssets && activeVariant.heroAssets.length > MAX_SLIDES) {
			maxTransform = (activeVariant.heroAssets.length * SLIDE_MARGIN_HEIGHT) - (MAX_SLIDES * SLIDE_MARGIN_HEIGHT);
			slideshowTransform = activeImageIndex * SLIDE_MARGIN_HEIGHT;
			transform = min([maxTransform, slideshowTransform]) * -1;
		}

		const previousButton = (activeVariant && activeVariant.heroAssets && activeVariant.heroAssets.length > MAX_SLIDES) && (
			<button
				onClick={this.onClickPrevious}
				title="previous"
				name="previous"
				className="full-width mb3">
				<img
					className="db m--auto flip"
					src="/images/icon-arrow-pdp-hero.svg"
				/>
			</button>
		);

		const nextButton = (activeVariant && activeVariant.heroAssets && activeVariant.heroAssets.length > MAX_SLIDES) && (
			<button
				onClick={this.onClickNext}
				title="next"
				name="next"
				className="full-width mt3">
				<img
					className="db m--auto"
					src="/images/icon-arrow-pdp-hero.svg"
				/>
			</button>
		);

		const desktopImageCarousel = (
			<div className="show--xl">
				<div className="media media--horizontal media--forward gutter--none align--middle">
					<div className="media__fixed reveal__slide reveal__slide--medium reveal__delay--2 multiply">
						{previousButton}
						<div className="product__hero-asset-slideshow-container">
							<div
								style={{transform: `translate3d(0, ${transform}px, 0)`}}
								className="product__hero-asset-slideshow multiply">
								{imageButtons}
							</div>
						</div>
						{nextButton}
					</div>
					<div className="pl4 media__fluid reveal__slide reveal__slide--medium reveal__delay--3 multiply">
						<div
							onClick={openProductHeroModal}
							className="cursor--pointer product__hero-zoom">
							{heroAsset}
						</div>
					</div>
				</div>
			</div>
		);

		const mobileImageCarousel = (activeVariant && activeVariant.heroAssets) && (
			<div
				key={activeVariantId}
				className="hide--xl">
				<div className="pr product__hero-mobile-slider">
					<Flickity
						sliderRef={slider => this.mobileSlider = slider}
						className="multiply"
						options={SLIDE_OPTIONS}>{activeVariant.heroAssets.map(asset => {
							let heroAsset;

							if (asset && asset._type === 'image') {
								heroAsset = (
									<div className="image size--1x1 multiply">
										<picture>
											<img
												src={`${asset.url}?w=1000&auto=format`}
												alt={asset.alt}
											/>
										</picture>
									</div>
								);
							} else if (asset && asset._type === 'video') {
								const __html = (`
									<video
										class="full-width"
										muted
										autoplay
										playsinline
										preload
										loop
										poster="${asset.posterImage}?w=1000&auto=format">
										<source
											src="${asset.url}"
											type="video/mp4"
										></source>
									</video>
								`);

								heroAsset = (
									<div
										className="full-width"
										dangerouslySetInnerHTML={{__html}}
									/>
								);
							}

							return (
								<div
									key={asset._key}
									className="full-width">
									{heroAsset}
								</div>
							);
						})}</Flickity>
					<button
						className="product__hero-carousel__previous"
						title="previous"
						name="previous"
						onClick={this.onClickPrevious}>
						<img
							className="db flip"
							alt="next"
							src="/images/icon-carousel-next.svg"
						/>
					</button>
					<button
						className="product__hero-carousel__next"
						title="next"
						name="next"
						onClick={this.onClickNext}>
						<img
							className="db"
							alt="next"
							src="/images/icon-carousel-next.svg"
						/>
					</button>
				</div>
			</div>
		);

		const authorizedDealer = enableAuthorizedDealer && (
			<div className="media media--forward media--horizontal mb2 gutter--small align--middle">
				{authorizedDealerLogo && (
					<div className="media__fixed pr">
						<img
							className="db product-hero__dealer-logo"
							alt={authorizedDealerLogo.alt}
							src={`${authorizedDealerLogo.url}?w=120&h=120`}
						/>
						<img
							className="db product-hero__dealer-check"
							src="/images/icon-arrow-dealer.svg"
						/>
					</div>
				)}
				<div className="media__fluid sans--small pl1">
					<p>{authorizedDealerTitle}</p>
					<p className="color--gray-text">{authorizedDealerSubtitle}</p>
				</div>
			</div>
		);

		const hero = (
			<Reveal>
				<div className="product__hero bg--gray-bg pt6 pb6 bb--gray-border vertical-align-container">
					<div className="full-width">
						<div className="grid-container contained">
							<div className="row align--middle">
								<div className="col c6--md mb4 mb0--md">
									{desktopImageCarousel}
									{mobileImageCarousel}
								</div>

								<div className="col c6--md c5--xl reveal__slide reveal__slide--medium reveal__delay--5">
									{authorizedDealer}
									<h2 className="domaine--large mb1">
										{brand && brand.map(brand => brand.title).join(', ')}
									</h2>
									<h1 className="color--gray-text sans--medium mb2">
										{title}
									</h1>
									<p className="domaine--medium affirm-price__before mb2">
										{!!productCompareAtCents && (
											<span className="mr1 o50 strike">
												{centsToPrice(productCompareAtCents)}
											</span>
										)}
										<span className="mr1">
											{centsToPrice(productCents)}
										</span>
									</p>
									<div className={classNames('row', {
										mb2: variants && variants.length > 1,
										mb3: !variants || variants.length <= 1,
									})}>
										<div
											className={classNames('col', {
												'c6--md': priceMatchDescription
											})}>
											<span
												className="affirm-as-low-as dib sans--small product-hero__price-col"
												data-page-type="product"
												data-amount={productCents}
											/>
										</div>
										{priceMatchDescription && (
											<div className="col c6--md">
												<div className="sans--small product-hero__price-col">
													<BlockContent blocks={priceMatchDescription} serializers={links} />
												</div>
											</div>
										)}
									</div>
									{(variants && variants.length > 1) && (
										<div className="mb3">
											<InventoryProductProvider
												productId={productId}
												children={({product, isAddingItem, addItemAndOpenCart}) => (
													<ProductOptionsForm
														activeVariantId={this.state.activeVariantId ? this.state.activeVariantId : null}
														variants={this.props.variants}
														inventoryProduct={product}
														optionDropdowns={optionDropdowns}
														selectedOptions={selectedOptions}
														onSelectOption={this.onSelectOption}
													/>
											)}
												/>
										</div>
									)}
									<InventoryProductProvider
										productId={productId}
										renderLoading={() => (
											<div className="button__height--primary color--gray-text sans--small fw--500">
												<p>Loading inventory…</p>
											</div>
										)}
										children={({product, isAddingItem, addItemAndOpenCart}) => (
											<Transition
												in
												mountOnEnter
												appear
												timeout={0}>{state => (
												<div
													style={{
														...DEFAULT_STYLE,
														...TRANSITION_STYLES[state]
													}}>
													<ProductHeroForm
														brandName={brand ? brand.map(brand => brand.title).join(', ') : undefined}
														productId={productId}
														variantId={activeVariantId}
														inventoryProduct={product}
														isAddingItem={isAddingItem}
														addItemAndOpenCart={addItemAndOpenCart}
														notifyMeCta={notifyMeCta}
														priceMatchCta={priceMatchCta}
													/>
												</div>
											)}</Transition>
										)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Reveal>
		);

		const priceMatch = (
			<div className="bg--gray-bg bb--gray-border pt6 pb6 pt3--md pb3--md">
				<div className="grid-container contained">
					<div className="row">
						<div className="col c3--md mb2 mb0--md">
							<h3 className="domaine--small show--md">{guaranteeTitle}</h3>
							<h3 className="domaine--medium hide--md">{guaranteeTitle}</h3>
						</div>
						{guaranteeColumns.map(col => (
							<div key={col._key} className="col c3--md mb1 mb0--md">
								<p className="sans--xsmall fw--500 uppercase">{col.title}</p>
								<p className="sans--small color--gray-text">{col.description}</p>
							</div>
						))}
					</div>
				</div>
			</div>
		);

		const modal = (
			<ImageGalleryModal
				active={isProductHeroModalOpen}
				assets={activeVariant ? activeVariant.heroAssets : []}
				initialIndex={activeImageIndex}
				onClose={closeProductHeroModal}
				sliderRef={slider => this.modalSlider = slider}
				onNext={this.onClickNext}
				onPrevious={this.onClickPrevious}
				onChange={this.setAssetIndex}
			/>
		);

		return (
			<React.Fragment>
				{hero}
				{priceMatch}
				{modal}
			</React.Fragment>
		);
	}
}

const mapStateToProps = ({
	isProductHeroModalOpen,
}) => ({
	isProductHeroModalOpen,
});

const mapStoreToProps = ({
	openProductHeroModal,
	closeProductHeroModal,
}) => ({
	openProductHeroModal,
	closeProductHeroModal,
});

export default connectToStore(mapStateToProps, mapStoreToProps)(ProductHero);